import {
  type FileInfo,
  type SimilarSuggestion,
  SUGGESTION_STATUSES,
  TICKET_KINDS,
  TICKET_URGENCIES,
  type TicketDetail,
  type TicketKind,
  type TicketUrgency,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { Paperclip, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { supportUi, useSupportUi } from '../../lib/support';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { ErrorBox, toast } from '../../ui/feedback';
import { Field, Input, Select, Textarea } from '../../ui/form';

/** Файл к обращению: загружается после того, как обращение создано, и привязывается к нему (БЗ-22) */
export async function uploadSupportFiles(entityType: string, entityId: string, files: File[]) {
  let failed = 0;
  for (const f of files) {
    const form = new FormData();
    form.append('entityType', entityType);
    form.append('entityId', entityId);
    form.append('file', f, f.name);
    try {
      await api<FileInfo>('/files', { method: 'POST', form });
    } catch {
      failed++;
    }
  }
  if (failed)
    toast(
      `Не удалось приложить файлов: ${failed}. Обращение отправлено, файлы можно добавить в нём.`,
      'info',
    );
}

function FilePicker({ files, onChange }: { files: File[]; onChange: (f: File[]) => void }) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <div className="flex flex-col gap-2">
      <input
        ref={ref}
        type="file"
        multiple
        accept="image/*,.pdf,.txt,.xlsx,.docx"
        className="hidden"
        onChange={(e) => {
          if (e.target.files) onChange([...files, ...Array.from(e.target.files)].slice(0, 5));
          if (ref.current) ref.current.value = '';
        }}
      />
      <div>
        <Button
          icon={<Paperclip className="size-4" />}
          onClick={() => ref.current?.click()}
          disabled={files.length >= 5}
        >
          Приложить снимок экрана или файл
        </Button>
      </div>
      {files.length ? (
        <ul className="flex flex-col gap-1">
          {files.map((f, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: у выбранных файлов нет своего номера, одинаковые имена возможны
            <li key={`${f.name}-${i}`} className="flex items-center justify-between gap-2 text-[13px]">
              <span className="[overflow-wrap:anywhere]">{f.name}</span>
              <button
                type="button"
                aria-label={`Убрать файл ${f.name}`}
                className="inline-flex size-11 shrink-0 items-center justify-center rounded-md text-ink-2 hover:bg-tile md:size-8"
                onClick={() => onChange(files.filter((_, j) => j !== i))}
              >
                <X className="size-4" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

const deviceText = () =>
  `${navigator.userAgent.slice(0, 200)}; окно ${window.innerWidth}×${window.innerHeight}`.slice(0, 300);

/** Обращение в поддержку с любой страницы: тему страницы, роль и устройство портал подставляет сам (БЗ-21, БЗ-22) */
function TicketDialog() {
  const { dialog } = useSupportUi();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const nav = useNavigate();
  const qc = useQueryClient();
  const [subject, setSubject] = useState('');
  const [kind, setKind] = useState<TicketKind>('question');
  const [urgency, setUrgency] = useState<TicketUrgency>('normal');
  const [body, setBody] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const open = dialog === 'ticket';
  useEffect(() => {
    if (open) {
      setSubject('');
      setKind('question');
      setUrgency('normal');
      setBody('');
      setFiles([]);
      setErrors({});
    }
  }, [open]);
  const send = useMutation({
    mutationFn: async () => {
      const t = await api<TicketDetail>('/support/tickets', {
        method: 'POST',
        body: {
          subject,
          kind,
          urgency,
          body,
          pagePath: path,
          pageTitle: document.title.slice(0, 200),
          device: deviceText(),
        },
      });
      if (files.length) await uploadSupportFiles('support_ticket', t.id, files);
      return t;
    },
    onSuccess: (t) => {
      toast(`Обращение ${t.number} отправлено`);
      qc.invalidateQueries({ queryKey: ['support'] });
      supportUi.closeDialog();
      nav({ to: '/support/tickets/$id', params: { id: t.id } });
    },
    onError: (e) =>
      setErrors(e instanceof ApiError ? { ...e.fieldErrors(), _: e.message } : { _: 'Не удалось отправить' }),
  });
  return (
    <Dialog
      open={open}
      onOpenChange={(v) => !v && supportUi.closeDialog()}
      title="Написать в поддержку"
      description="Страницу, вашу роль и устройство мы приложим сами"
      footer={
        <>
          <Button variant="ghost" onClick={() => supportUi.closeDialog()}>
            Отмена
          </Button>
          <Button variant="primary" loading={send.isPending} onClick={() => send.mutate()}>
            Отправить
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Тема" htmlFor="tk-subject" error={errors.subject}>
          <Input
            className="h-11 md:h-10"
            id="tk-subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            maxLength={200}
            invalid={!!errors.subject}
          />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Что у вас" htmlFor="tk-kind">
            <Select
              className="h-11 md:h-10"
              id="tk-kind"
              value={kind}
              onChange={(e) => setKind(e.target.value as TicketKind)}
            >
              {Object.entries(TICKET_KINDS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Насколько срочно" htmlFor="tk-urg">
            <Select
              className="h-11 md:h-10"
              id="tk-urg"
              value={urgency}
              onChange={(e) => setUrgency(e.target.value as TicketUrgency)}
            >
              {Object.entries(TICKET_URGENCIES).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <p className="-mt-2 text-[12px] text-ink-2">
          {urgency === 'blocking' ? 'Ответим в течение 2 часов.' : 'Ответим в течение 4 рабочих часов.'}
        </p>
        <Field label="Что случилось или что нужно" htmlFor="tk-body" error={errors.body}>
          <Textarea
            id="tk-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={4000}
            invalid={!!errors.body}
            rows={5}
          />
        </Field>
        <FilePicker files={files} onChange={setFiles} />
        {errors._ && !errors.subject && !errors.body ? <ErrorBox error={new Error(errors._)} /> : null}
      </div>
    </Dialog>
  );
}

/** Предложение улучшения: перед отправкой портал показывает похожие, чтобы голосовать, а не дублировать (БЗ-28) */
function SuggestionDialog() {
  const { dialog } = useSupportUi();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const nav = useNavigate();
  const qc = useQueryClient();
  const [title, setTitle] = useState('');
  const [problem, setProblem] = useState('');
  const [proposal, setProposal] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [debounced, setDebounced] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const open = dialog === 'suggestion';
  useEffect(() => {
    if (open) {
      setTitle('');
      setProblem('');
      setProposal('');
      setFiles([]);
      setErrors({});
    }
  }, [open]);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(title.trim()), 400);
    return () => clearTimeout(t);
  }, [title]);
  const similar = useQuery({
    queryKey: ['support', 'similar', debounced],
    queryFn: () => api<SimilarSuggestion[]>('/support/suggestions/similar', { query: { q: debounced } }),
    enabled: open && debounced.length >= 3,
  });
  const send = useMutation({
    mutationFn: async () => {
      const s = await api<{ id: string; number: string }>('/support/suggestions', {
        method: 'POST',
        body: { title, problem, proposal, pagePath: path },
      });
      if (files.length) await uploadSupportFiles('support_suggestion', s.id, files);
      return s;
    },
    onSuccess: (s) => {
      toast(`Предложение ${s.number} отправлено`);
      qc.invalidateQueries({ queryKey: ['support'] });
      supportUi.closeDialog();
      nav({ to: '/support/suggestions/$id', params: { id: s.id } });
    },
    onError: (e) =>
      setErrors(e instanceof ApiError ? { ...e.fieldErrors(), _: e.message } : { _: 'Не удалось отправить' }),
  });
  return (
    <Dialog
      open={open}
      onOpenChange={(v) => !v && supportUi.closeDialog()}
      title="Предложить улучшение"
      description="Что неудобно и как, по-вашему, лучше. Другие коллеги смогут проголосовать"
      footer={
        <>
          <Button variant="ghost" onClick={() => supportUi.closeDialog()}>
            Отмена
          </Button>
          <Button variant="primary" loading={send.isPending} onClick={() => send.mutate()}>
            Отправить
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Название" htmlFor="sg-title" error={errors.title}>
          <Input
            className="h-11 md:h-10"
            id="sg-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            invalid={!!errors.title}
          />
        </Field>
        {similar.data?.length ? (
          <div className="rounded-md border border-line bg-surface-2 p-3 text-[13px]">
            <p className="mb-1 font-medium">Похожее уже предложили. Лучше проголосуйте там:</p>
            <ul className="flex flex-col">
              {similar.data.map((s) => (
                <li key={s.id}>
                  <Link
                    to="/support/suggestions/$id"
                    params={{ id: s.id }}
                    onClick={() => supportUi.closeDialog()}
                    className="inline-flex min-h-11 items-center text-accent-text hover:underline md:min-h-0"
                  >
                    {s.number} {s.title} ({SUGGESTION_STATUSES[s.status].toLowerCase()}, голосов: {s.votes})
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        <Field label="Что сейчас неудобно" htmlFor="sg-problem" error={errors.problem}>
          <Textarea
            id="sg-problem"
            value={problem}
            onChange={(e) => setProblem(e.target.value)}
            maxLength={4000}
            invalid={!!errors.problem}
            rows={3}
          />
        </Field>
        <Field label="Что предлагаете" htmlFor="sg-proposal" error={errors.proposal}>
          <Textarea
            id="sg-proposal"
            value={proposal}
            onChange={(e) => setProposal(e.target.value)}
            maxLength={4000}
            invalid={!!errors.proposal}
            rows={3}
          />
        </Field>
        <FilePicker files={files} onChange={setFiles} />
        {errors._ && !errors.title && !errors.problem && !errors.proposal ? (
          <ErrorBox error={new Error(errors._)} />
        ) : null}
      </div>
    </Dialog>
  );
}

export function SupportDialogs() {
  return (
    <>
      <TicketDialog />
      <SuggestionDialog />
    </>
  );
}
